import APPLICATION_CONSTANTS from '../applicationConstants';
import { getUserLocationByIp } from './geocodeUtils';
import { shouldSkipRedirect } from './urlParamUtils';

function getUserRedirectionTargetUrl(userCountryCode, currentLanguage) {
  console.log('[Redirection] Getting target URL for:', {
    userCountryCode,
    currentLanguage
  });

  let targetURL;

  // LATAM countries with their default languages
  const LATAM_COUNTRIES = {
    // Spanish-speaking countries
    'AR': 'es-es', // Argentina
    'BO': 'es-es', // Bolivia
    'CL': 'es-es', // Chile
    'CO': 'es-es', // Colombia
    'CR': 'es-es', // Costa Rica
    'CU': 'es-es', // Cuba
    'DO': 'es-es', // Dominican Republic
    'EC': 'es-es', // Ecuador
    'SV': 'es-es', // El Salvador
    'GT': 'es-es', // Guatemala
    'HN': 'es-es', // Honduras
    // 'MX': 'es-es', // Mexico -- removed due to conflict with AAV Mexico
    'NI': 'es-es', // Nicaragua
    'PA': 'es-es', // Panama
    'PY': 'es-es', // Paraguay
    'PE': 'es-es', // Peru
    'UY': 'es-es', // Uruguay
    'VE': 'es-es', // Venezuela

    // English-speaking countries
    'AG': 'en-us', // Antigua and Barbuda
    'BS': 'en-us', // Bahamas
    'BB': 'en-us', // Barbados
    'BZ': 'en-us', // Belize
    'DM': 'en-us', // Dominica
    'GD': 'en-us', // Grenada
    'GY': 'en-us', // Guyana
    'JM': 'en-us', // Jamaica
    'KN': 'en-us', // Saint Kitts and Nevis
    'LC': 'en-us', // Saint Lucia
    'VC': 'en-us', // Saint Vincent and the Grenadines
    'TT': 'en-us', // Trinidad and Tobago

    // Bilingual countries (default to most common language but allow both)
    'PR': 'es-es', // Puerto Rico (Spanish primary, English also official)
    'SR': 'en-us', // Suriname (Dutch official, but English widely used)
  };

  const upperCountryCode = userCountryCode.toUpperCase();

  // Handle LATAM countries
  if (upperCountryCode in LATAM_COUNTRIES) {
    // If user's current language doesn't match country's default,
    // respect their choice between en-us and es-es
    // if (currentLanguage === 'en-us' || currentLanguage === 'es-es') {
    //   return `/aav_latam/${currentLanguage}`;
    // }
    // Otherwise use country's default
    return `/aav_latam/${LATAM_COUNTRIES[upperCountryCode]}`;
  }

  switch (upperCountryCode) {
    case 'US':
      targetURL = '/aav_us/en-us'
      break;
    case 'DE':
      targetURL = '/aav_de/de-de';
      break;
    case 'MX':
      // case 'CO':
      // case 'AR':
      // case 'PE':
      // case 'EC':
      // case 'CL':
      targetURL = '/aav_mx/es-mx';
      break;
    case 'GB':
    case 'UK':
      targetURL = '/aav_uk2/en-gb';
      break;
    case 'CA':
      targetURL =
        currentLanguage === 'fr-ca' ? '/aav_ca2/fr-ca' : '/aav_ca2/en-ca';
      break;
    case 'BR':
      targetURL = '/aav_br2/pt-br';
      break;
    case 'ES':
      targetURL = '/aav_es2/es-es';
      break;
    case 'FR':
      targetURL = '/aav_fr2/fr-fr';
      break;
    case 'IN':
      targetURL = '/aav_in2/en-in';
      break;
    case 'IT':
      targetURL = '/aav_it2/it-it';
      break;
    case 'AU':
      targetURL = '/aav_au2/en-au';
      break;
    case 'JP':
      targetURL = '/aav_jp2/ja-jp';
      break;
    case 'TH':
      targetURL = '/aav_th/th-th';
      break;
    case 'TR':
      targetURL = '/aav_tr/tr-tr';
      break;
    case 'HK':
    case 'TW':
    case 'CN':
      targetURL = '/aav_china2/zh-cn';
      break;
    case 'ID':
      targetURL = '/aav_id2/id-id';
      break;
    case 'VN':
      targetURL = '/aav_vn2/vi-vn'
      break;
    case 'RU':
      targetURL = '/aav_ru/ru-ru';
      break;
    case 'SA':
      targetURL = '/aav_ksa/ar-sa';
      break;
    case 'SG':
      targetURL = '/aav_sg/en-sg';
      break;
    default:
      return null;
  }

  console.log('[Redirection] Determined target URL:', targetURL);
  return targetURL;
}

export function redirectUser(userCountryCode, config) {
  console.log('[Redirection] Attempting redirect with:', {
    userCountryCode,
    tenantCountry: config.country_code,
    currentURL: window.location.href
  });

  /**
   * If the locatorTenancy has a country code flag AND the current country is not the US,
   * compare the locatorTenancy code with the current country code. If they do not match, find the correct URL for
   */
  const tenantCountry = config.country_code;
  const URLroot = window.location.origin;
  const currentURL = window.location.href.replace(URLroot, "");
  let targetURL = getUserRedirectionTargetUrl(userCountryCode, APPLICATION_CONSTANTS.GLOBAL.CURRENT_LANGUAGE);

  if (!targetURL) {
    console.log('[Redirection] No target URL found, skipping redirect');
    return;
  }

  console.log('[Redirection] Comparing URLs:', {
    current: currentURL,
    target: `${targetURL}/${window.location.hash}`,
    willRedirect: userCountryCode !== tenantCountry
  });

  if (userCountryCode !== tenantCountry && currentURL !== `${targetURL}/${window.location.hash}`) {
    console.log('[Redirection] Redirecting to:', `${URLroot}${targetURL}/${window.location.hash}`);
    window.location.href = `${URLroot}${targetURL}/${window.location.hash}`;
  } else {
    console.log('[Redirection] No redirect needed');
    if (window.location.pathname === "/") {
      trackPageView(config, "/landing");
    }
  }
}

/**
 * 
 */
export async function checkForUserRedirection(config) {
  console.log('[Redirection] Starting redirection check with config:', {
    country_code: config.country_code,
    is_an_aav_tenant: config.is_an_aav_tenant,
    country_geo_constraint: config.country_geo_constraint
  });

  const { country_code, country_geo_constraint, is_an_aav_tenant } = config;

  const geoIpResponse = await getUserLocationByIp();
  console.log('[Redirection] GeoIP response:', geoIpResponse);

  const response = geoIpResponse ?? { country_code: 'US' };
  console.log('[Redirection] Using location:', response);

  if (country_code && is_an_aav_tenant) {
    let countryCodeDoesNotMatch = false;

    if (Array.isArray(country_code)) {
      // If country_code is an array, check if user's country is NOT in the array
      countryCodeDoesNotMatch = !country_code.includes(response.country_code);
    } else {
      // Original string comparison
      countryCodeDoesNotMatch = country_code !== response.country_code;
    }

    console.log('[Redirection] Initial country code match:', {
      tenant: country_code,
      user: response.country_code,
      matches: !countryCodeDoesNotMatch
    });

    if (country_geo_constraint && countryCodeDoesNotMatch) {
      countryCodeDoesNotMatch = !country_geo_constraint.some(x => x.toUpperCase() === response.country_code);
      console.log('[Redirection] Checked geo constraints:', {
        constraints: country_geo_constraint,
        userCountry: response.country_code,
        stillDoesNotMatch: countryCodeDoesNotMatch
      });
    }

    if (countryCodeDoesNotMatch && !shouldSkipRedirect()) {
      console.log('[Redirection] Initiating redirect for country mismatch');
      redirectUser(response.country_code, config);
    } else {
      console.log('[Redirection] No redirect needed:', {
        countryCodeDoesNotMatch,
        skipRedirect: shouldSkipRedirect()
      });
    }
  } else {
    console.log('[Redirection] Skipping redirect check - not an AAV tenant or no country code');
  }
};
